import {Component, OnInit} from '@angular/core';
import {Router} from '@angular/router';
import {
    CampaignGroupListItemDto,
    DatastoreDto,
    EngineCampaignGroupService,
    Partner
} from '@tj/tj-dmp-lib-api';
import {ConfirmationService, MenuItem, MessageService} from 'primeng/api';
import {TranslateService} from '@ngx-translate/core';

@Component({
    templateUrl: './campaign-group.component.html',
    styleUrls: ['./campaign-group.component.scss'],
    standalone: false
})
export class CampaignGroupComponent implements OnInit {

    breadcrumb: MenuItem[] = [
        {label: this.translate.instant('menu.marketing.label')},
        {label: this.translate.instant('menu.marketing.campaignGroups')}
    ];
    description = this.translate.instant('campaignGroup.listDescription');

    tenant: string;
    partner: string;
    groups: CampaignGroupListItemDto[] = [];
    totalRecords = 0;
    loading = false;
    searchText = '';
    rows = 10;
    first = 0;
    rowsPerPageOptions = [10, 25, 50];
    readonly descriptionMaxLength = 80;

    constructor(
        private readonly engineCampaignGroupService: EngineCampaignGroupService,
        private readonly confirmationService: ConfirmationService,
        private readonly messageService: MessageService,
        private readonly router: Router,
        private readonly translate: TranslateService
    ) {
        this.translate.onLangChange.subscribe(() => this.rebuildTranslatedHeader());
    }

    private rebuildTranslatedHeader(): void {
        this.breadcrumb = [
            {label: this.translate.instant('menu.marketing.label')},
            {label: this.translate.instant('menu.marketing.campaignGroups')}
        ];
        this.description = this.translate.instant('campaignGroup.listDescription');
    }

    ngOnInit(): void {
        if (this.tenant) {
            this.loadGroups();
        }
    }

    updateTenant(datastore: DatastoreDto): void {
        this.tenant = datastore?.code;
        this.partner = undefined;
        this.first = 0;
        this.loadGroups();
    }

    updatePartner(partner: Partner): void {
        this.partner = partner?.code;
        this.first = 0;
        this.loadGroups();
    }

    onSearchChange(value: string): void {
        this.searchText = value || '';
        this.first = 0;
        this.loadGroups();
    }

    clearSearch(): void {
        this.onSearchChange('');
    }

    onPageChange(event: { first?: number; rows?: number }): void {
        this.first = event.first ?? 0;
        this.rows = event.rows ?? this.rows;
        this.loadGroups();
    }

    loadGroups(): void {
        if (!this.tenant || !this.partner) {
            this.groups = [];
            this.totalRecords = 0;
            return;
        }
        this.loading = true;
        const page = Math.floor(this.first / this.rows);
        this.engineCampaignGroupService.findAll(
            this.tenant,
            this.partner,
            this.searchText?.trim() || undefined,
            page,
            this.rows,
            'response'
        ).subscribe({
            next: (res) => {
                this.groups = res.body || [];
                const total = res.headers.get('X-Total-Count');
                this.totalRecords = total ? parseInt(total, 10) : this.groups.length;
                this.loading = false;
            },
            error: () => {
                this.loading = false;
                this.messageService.add({
                    severity: 'error',
                    summary: this.translate.instant('campaign.edit.message.errorSummary'),
                    detail: this.translate.instant('campaignGroup.loadErrorDetail')
                });
            }
        });
    }

    viewGroup(group: CampaignGroupListItemDto): void {
        this.router.navigate(['/h/marketing/campaign-group', this.tenant, group.id]);
    }

    formatDescription(description?: string): string {
        if (!description?.trim()) {
            return '-';
        }
        if (description.length <= this.descriptionMaxLength) {
            return description;
        }
        return `${description.slice(0, this.descriptionMaxLength).replace(/\s+$/, '')}…`;
    }

    isDescriptionTruncated(description?: string): boolean {
        return !!description && description.length > this.descriptionMaxLength;
    }

    editGroup(group: CampaignGroupListItemDto): void {
        this.router.navigate(['/h/marketing/campaign-group', this.tenant, group.id, 'edit']);
    }

    deleteGroup(group: CampaignGroupListItemDto): void {
        this.confirmationService.confirm({
            message: this.translate.instant('campaignGroup.confirmDelete', {name: group.name}),
            header: 'Confirmation',
            icon: 'pi pi-exclamation-triangle',
            accept: () => {
                this.engineCampaignGroupService.delete(this.tenant, group.id).subscribe({
                    next: () => {
                        this.messageService.add({
                            severity: 'success',
                            summary: this.translate.instant('campaign.edit.message.successSummary'),
                            detail: this.translate.instant('campaignGroup.deletedDetail')
                        });
                        this.loadGroups();
                    },
                    error: () => {
                        this.messageService.add({
                            severity: 'error',
                            summary: this.translate.instant('campaign.edit.message.errorSummary'),
                            detail: this.translate.instant('campaignGroup.deleteErrorDetail')
                        });
                    }
                });
            }
        });
    }
}
